import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Copy, Trash2, Key, Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function APISettings() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [formData, setFormData] = useState({
    name: '',
  });

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API}/api-keys`);
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/api-keys`, formData);
      toast.success('API key created successfully!');
      setDialogOpen(false);
      setFormData({ name: '' });
      fetchApiKeys();

      // Auto-show the new key
      setVisibleKeys({ ...visibleKeys, [response.data.id]: true });
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error('Failed to create API key');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/api-keys/${id}`);
      toast.success('API key deleted successfully!');
      fetchApiKeys();
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('API key copied to clipboard!');
  };

  const toggleKeyVisibility = (keyId) => {
    setVisibleKeys({ ...visibleKeys, [keyId]: !visibleKeys[keyId] });
  };

  const maskKey = (key) => {
    if (key.length <= 8) return '***';
    return key.substring(0, 8) + '...' + key.substring(key.length - 4);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="api-settings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900">API Settings</h1>
          <p className="text-slate-600 mt-2">Manage API keys for integrations</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1680e5] hover:bg-[#1680e5]/90" data-testid="create-api-key-button">
              <Plus className="h-4 w-4 mr-2" />
              Create API Key
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New API Key</DialogTitle>
              <DialogDescription>Generate a new API key for your integrations</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Key Name *</Label>
                <Input
                  id="name"
                  placeholder="Production Key"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="api-key-name-input"
                />
                <p className="text-xs text-slate-500">
                  A descriptive name to help you identify this key
                </p>
              </div>
              <Button type="submit" className="w-full" data-testid="api-key-submit-button">
                Generate API Key
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-[#1680e5]" />
            API Key Usage
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-slate-700">
          <p>• API keys are used to authenticate requests to the WMS API</p>
          <p>• Include the API key in the <code className="bg-white px-1 py-0.5 rounded">X-API-Key</code> header</p>
          <p>• Keep your API keys secure and never share them publicly</p>
          <p>• You can create multiple keys for different environments or integrations</p>
        </CardContent>
      </Card>

      {/* API Keys List */}
      {apiKeys.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Key className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No API keys yet</h3>
            <p className="text-slate-600 mb-4">Create your first API key to start using the API</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-[#1680e5] hover:bg-[#1680e5]/90"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create API Key
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Your API Keys</CardTitle>
            <CardDescription>{apiKeys.length} API key(s) configured</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {apiKeys.map((apiKey) => (
                <div
                  key={apiKey.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900">{apiKey.name}</h3>
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                        Active
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-sm bg-slate-100 px-3 py-1.5 rounded">
                        {visibleKeys[apiKey.id] ? apiKey.key : maskKey(apiKey.key)}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleKeyVisibility(apiKey.id)}
                        data-testid={`toggle-key-${apiKey.name}`}
                      >
                        {visibleKeys[apiKey.id] ? (
                          <EyeOff className="h-4 w-4 text-slate-600" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-600" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => copyToClipboard(apiKey.key)}
                        data-testid={`copy-key-${apiKey.name}`}
                      >
                        <Copy className="h-4 w-4 text-slate-600" />
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Created {new Date(apiKey.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(apiKey.id)}
                    data-testid={`delete-key-${apiKey.name}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Example Usage */}
      <Card>
        <CardHeader>
          <CardTitle>Example Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">cURL Example</Label>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs overflow-x-auto mt-2">
{`curl -X GET "${BACKEND_URL}/api/orders" \\
  -H "X-API-Key: your_api_key_here"`}
              </pre>
            </div>
            <div>
              <Label className="text-xs text-slate-600">JavaScript Example</Label>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs overflow-x-auto mt-2">
{`fetch('${BACKEND_URL}/api/orders', {
  headers: {
    'X-API-Key': 'your_api_key_here'
  }
})`}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
